import { useMemo } from "react";
import { useClaims } from "auth/ClaimsProvider";
import useGetTenantCurrent from "@features/unified-user-header-feature/hooks/useGetTenantCurrent";
import { TENANT_ID_UNDEFINED } from "@features/impersonation-feature/constants";
import { getInitials } from "@features/impersonation-feature/helpers";
import { hasAccountIdentity } from "@features/impersonation-feature/accountIdentity";
import {
  Account,
  ImpersonationSelection,
  Persona,
} from "@features/impersonation-feature/types";

export function useImpersonationOrigin(): ImpersonationSelection | null {
  const {
    getClaimValue,
    getClaimValueAsNumber,
    isGuestUser,
    isCustomerAdmin,
    isVirtualAdmin,
  } = useClaims();
  const { data: tenant, isInitialLoading: isInitialLoadingTenant } =
    useGetTenantCurrent();

  const accountId = getClaimValueAsNumber("accountId");
  const accountName = getClaimValue("accountName");
  const axNumber = getClaimValue("axNumber");
  const userId = getClaimValue("userId");
  const contactId = getClaimValueAsNumber("contactId");
  const shiComEmployeeId: Persona["shiComEmployeeId"] =
    getClaimValueAsNumber("shiComEmployeeId") ?? null;
  const firstName = getClaimValue("firstName");
  const lastName = getClaimValue("lastName");
  const emailAddress = getClaimValue("userEmail");

  const isGuest = isGuestUser();
  const virtualAdmin = isVirtualAdmin();
  const isAdmin = isCustomerAdmin();

  return useMemo<ImpersonationSelection | null>(() => {
    const hasUserIdentity = !(
      accountId == null &&
      userId == null &&
      (contactId == null || contactId === -1) &&
      !firstName &&
      !lastName &&
      !emailAddress &&
      !shiComEmployeeId
    );

    if (!hasUserIdentity || isInitialLoadingTenant) {
      return null;
    }

    const isUniqueId = !!userId;
    const personaId: Persona["id"] = virtualAdmin
      ? (accountId ?? null)
      : isUniqueId
        ? userId
        : null;
    const personaType: Persona["type"] = virtualAdmin
      ? "account"
      : isUniqueId
        ? "guid"
        : null;

    const user = {
      id: personaId,
      type: personaType,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      email: emailAddress ?? null,
      photo: null,
      initials: getInitials(
        isAdmin,
        virtualAdmin,
        isGuest,
        firstName,
        lastName,
      ),
      isGuest,
      isAdmin,
      isVirtualAdmin: virtualAdmin,
      shiComEmployeeId,
    } satisfies Persona;

    const account =
      tenant?.accounts?.find(
        (p) => p.autotaskAccountId != null && p.autotaskAccountId === accountId,
      ) ?? null;

    const accounts: Account[] =
      tenant?.accounts == null
        ? accountId != null &&
          hasAccountIdentity(accountId, accountName, axNumber)
          ? [
              {
                id: accountId,
                name: accountName ?? null,
                axNumber: axNumber ?? null,
                isPrimary: null,
                rowKey: null,
              } satisfies Account,
            ]
          : []
        : tenant.accounts.map(
            (p) =>
              ({
                id: p.autotaskAccountId ?? 0,
                name: p.name ?? null,
                axNumber: p.axNumber ?? null,
                isPrimary: p.isPrimaryAccount ?? false,
                rowKey: null,
              }) satisfies Account,
          );

    return {
      tenant: {
        id: tenant?.tenantId ?? TENANT_ID_UNDEFINED,
        name: tenant?.name ?? null,
        onBoardingStatus: tenant?.onboardingStatus ?? null,
        accounts,
      },
      user,
      account:
        accountId == null
          ? null
          : {
              id: accountId,
              name: accountName ?? null,
              axNumber: axNumber ?? null,
              isPrimary: account?.isPrimaryAccount ?? null,
              rowKey: null,
            },
    };
  }, [
    isInitialLoadingTenant,
    tenant,
    accountId,
    accountName,
    axNumber,
    userId,
    contactId,
    emailAddress,
    firstName,
    lastName,
    shiComEmployeeId,
    isGuest,
    isAdmin,
    virtualAdmin,
  ]);
}
